import type { ClientOrderId } from "@src/features/trade/hyperliquid/clientOrderId"
import {
  HL_BUILDER_FEE,
  HL_MARKET_SLIPPAGE,
  getHLBuilderAddress,
  getHLBuilderParams,
} from "@src/features/trade/hyperliquid/constants/config"
import {
  type EntryOrder,
  getEntryOrderSubmissionPrice,
  getEntryOrderTimeInForce,
} from "@src/features/trade/hyperliquid/orderIntent"
import { formatHLSize } from "@src/features/trade/hyperliquid/orderSizing"
import type { Result } from "@src/features/trade/hyperliquid/types"
import type { TradingSigner } from "@src/features/tradingSigner"
import { logger } from "@src/utils/logger"
import { createHyperliquidExchangeClient } from "./hyperliquidClient"

export type EntryOrderResult =
  | {
      kind: "filled"
      avgPrice: string
      totalSz: string
      oid: number
      autoCloseError?: string
    }
  | {
      kind: "resting"
      oid: number
      autoCloseError?: string
    }

export type ReduceOrderResult =
  | EntryOrderResult
  | {
      kind: "partial"
      avgPrice: string
      totalSz: string
      oid: number
    }

// Tied to @nktkas/hyperliquid 0.32.x error text returned by HL.
const BUILDER_NOT_APPROVED_PATTERN = /builder fee has not been approved/i

export function needsBuilderApproval(errorMessage: string | null | undefined) {
  return !!errorMessage && BUILDER_NOT_APPROVED_PATTERN.test(errorMessage)
}

// Switches the account into a collateral mode that can margin builder-dex
// (e.g. trade.xyz) markets. Idempotent at the account level; the
// positionScreenMachine only calls this once, after a read shows it is needed.
export async function setBuilderDexAbstraction(
  signer: TradingSigner
): Promise<Result<void>> {
  return withExchangeClient(
    signer,
    "setBuilderDexAbstraction",
    async (exchange) => {
      await exchange.agentSetAbstraction({ abstraction: "u" })
      return okVoidResult()
    }
  )
}

function requireBuilderParams(): { b: `0x${string}`; f: number } {
  const params = getHLBuilderParams()
  if (!params) {
    throw new Error(
      "Hyperliquid builder address is not configured (NEXT_PUBLIC_HL_BUILDER_ADDRESS)"
    )
  }
  return params
}

export async function approveHLBuilderFee(
  signer: TradingSigner
): Promise<Result<void>> {
  return withExchangeClient(signer, "approveBuilderFee", async (exchange) => {
    const builderAddress = getHLBuilderAddress()
    if (!builderAddress) {
      return { ok: false, error: "Builder address not configured" }
    }
    // HL_BUILDER_FEE is in tenths of basis points (1 unit = 0.001%).
    const maxFeeRate = `${HL_BUILDER_FEE / 1000}%`
    await exchange.approveBuilderFee({
      builder: builderAddress,
      maxFeeRate,
    })
    return okVoidResult()
  })
}

export async function closePosition(
  signer: TradingSigner,
  params: {
    orderAssetId: number
    szDecimals: number
    isLong: boolean
    size: string
    currentPrice: number
    cloid?: ClientOrderId
  }
): Promise<Result<{ avgPrice: string; totalSz: string }>> {
  return withExchangeClient(signer, "closePosition", async (exchange) => {
    const size = formatAbsoluteHLSize(params.size, params.szDecimals)
    const slippagePrice = params.isLong
      ? params.currentPrice * (1 - HL_MARKET_SLIPPAGE)
      : params.currentPrice * (1 + HL_MARKET_SLIPPAGE)

    const builder = requireBuilderParams()
    const result = await exchange.order({
      orders: [
        {
          a: params.orderAssetId,
          b: !params.isLong,
          p: formatHLPrice(slippagePrice, params.szDecimals),
          s: size,
          r: true,
          t: { limit: { tif: "Ioc" } },
          ...(params.cloid ? { c: params.cloid } : {}),
        },
      ],
      grouping: "na",
      builder,
    })

    return parseFilledOrderStatus(getFirstOrderStatus(result), size)
  })
}

export async function updateLeverage(
  signer: TradingSigner,
  params: {
    orderAssetId: number
    leverage: number
  }
): Promise<Result<void>> {
  return withExchangeClient(signer, "updateLeverage", async (exchange) => {
    await exchange.updateLeverage({
      asset: params.orderAssetId,
      // Isolated margin: each position is collateralized by its own margin
      // rather than sharing one cross-margin account balance, so a single
      // position's liquidation can't drain the rest of the account.
      isCross: false,
      leverage: params.leverage,
    })

    return okVoidResult()
  })
}

export async function placeEntryOrder(
  signer: TradingSigner,
  params: {
    orderAssetId: number
    szDecimals: number
    isLong: boolean
    size: string
    entryOrder: EntryOrder
    cloid?: ClientOrderId
    takeProfitPrice?: number
    stopLossPrice?: number
    takeProfitCloid?: ClientOrderId
    stopLossCloid?: ClientOrderId
  }
): Promise<Result<EntryOrderResult>> {
  return withExchangeClient(signer, "placeEntryOrder", async (exchange) => {
    const size = formatHLSize(params.size, params.szDecimals)
    const autoCloseOrders = buildTPSLTriggerOrders({
      orderAssetId: params.orderAssetId,
      szDecimals: params.szDecimals,
      isLong: params.isLong,
      size,
      takeProfitPrice: params.takeProfitPrice,
      stopLossPrice: params.stopLossPrice,
      takeProfitCloid: params.takeProfitCloid,
      stopLossCloid: params.stopLossCloid,
    })

    const result = await submitPositionOrder(exchange, {
      ...params,
      size,
      reduceOnly: false,
      autoCloseOrders,
    })

    const statuses = getOrderStatuses(result)
    const openResult = parseOpenOrderStatus(statuses?.[0], params.entryOrder)
    if (!openResult.ok) {
      return openResult
    }

    const autoCloseError = getAttachedTPSLError(
      statuses,
      autoCloseOrders.length
    )
    return {
      ok: true,
      data: {
        ...openResult.data,
        ...(autoCloseError ? { autoCloseError } : {}),
      },
    }
  })
}

export async function placeReduceOrder(
  signer: TradingSigner,
  params: {
    orderAssetId: number
    szDecimals: number
    isLong: boolean
    size: string
    entryOrder: EntryOrder
    cloid?: ClientOrderId
  }
): Promise<Result<ReduceOrderResult>> {
  return withExchangeClient<ReduceOrderResult>(
    signer,
    "placeReduceOrder",
    async (exchange) => {
      const size = formatHLSize(params.size, params.szDecimals)
      const result = await submitPositionOrder(exchange, {
        ...params,
        size,
        reduceOnly: true,
        autoCloseOrders: [],
      })
      const parsed = parseOpenOrderStatus(
        getFirstOrderStatus(result),
        params.entryOrder
      )
      if (
        parsed.ok &&
        parsed.data.kind === "filled" &&
        isPartialFill(parsed.data.totalSz, size)
      ) {
        return { ok: true, data: { ...parsed.data, kind: "partial" } }
      }
      return parsed
    }
  )
}

async function submitPositionOrder(
  exchange: ReturnType<typeof createHyperliquidExchangeClient>,
  params: {
    orderAssetId: number
    szDecimals: number
    isLong: boolean
    size: string
    entryOrder: EntryOrder
    cloid?: ClientOrderId
    reduceOnly: boolean
    autoCloseOrders: Array<ReturnType<typeof buildTPSLTriggerOrder>>
  }
) {
  const orderPrice = getEntryOrderSubmissionPrice(
    params.entryOrder,
    params.isLong
  )
  const builder = requireBuilderParams()
  return exchange.order({
    orders: [
      {
        a: params.orderAssetId,
        b: params.isLong,
        p: formatHLPrice(orderPrice, params.szDecimals),
        s: params.size,
        r: params.reduceOnly,
        t: {
          limit: {
            tif: getEntryOrderTimeInForce(params.entryOrder),
          },
        },
        ...(params.cloid ? { c: params.cloid } : {}),
      },
      ...params.autoCloseOrders,
    ],
    grouping: params.autoCloseOrders.length > 0 ? "normalTpsl" : "na",
    builder,
  })
}

type TPSLModifyParams = {
  orderId: number
  orderAssetId: number
  szDecimals: number
  isLong: boolean
  size: string
  leg: "tp" | "sl"
  price: number
}

type TPSLAdditionParams = {
  orderAssetId: number
  szDecimals: number
  isLong: boolean
  size: string
  leg: "tp" | "sl"
  price: number
  cloid?: ClientOrderId
}

export async function modifyTPSLOrders(
  signer: TradingSigner,
  params: {
    modifies: TPSLModifyParams[]
  }
): Promise<Result<void>> {
  if (params.modifies.length === 0) {
    return okVoidResult()
  }

  return withExchangeClient(signer, "modifyTPSLOrders", async (exchange) => {
    const result = await exchange.batchModify({
      modifies: params.modifies.map((modify) => ({
        oid: modify.orderId,
        order: buildTPSLTriggerOrder({
          orderAssetId: modify.orderAssetId,
          szDecimals: modify.szDecimals,
          isLong: modify.isLong,
          size: modify.size,
          price: modify.price,
          tpsl: modify.leg,
        }),
      })),
    })
    const statusError = getOrderStatusError(getOrderStatuses(result))
    return statusError ? { ok: false, error: statusError } : okVoidResult()
  })
}

export async function addTPSLOrders(
  signer: TradingSigner,
  params: {
    additions: TPSLAdditionParams[]
  }
): Promise<Result<void>> {
  if (params.additions.length === 0) {
    return okVoidResult()
  }

  return withExchangeClient(signer, "addTPSLOrders", async (exchange) => {
    const builder = requireBuilderParams()
    const orders = params.additions.map((addition) =>
      buildTPSLTriggerOrder({
        orderAssetId: addition.orderAssetId,
        szDecimals: addition.szDecimals,
        isLong: addition.isLong,
        size: addition.size,
        price: addition.price,
        cloid: addition.cloid,
        tpsl: addition.leg,
      })
    )
    return submitTPSLOrders(exchange, orders, builder)
  })
}

async function submitTPSLOrders(
  exchange: ReturnType<typeof createHyperliquidExchangeClient>,
  orders: Array<ReturnType<typeof buildTPSLTriggerOrder>>,
  builder: { b: `0x${string}`; f: number }
): Promise<Result<void>> {
  const result = await exchange.order({
    orders,
    grouping: "positionTpsl",
    builder,
  })

  const statuses = getOrderStatuses(result)
  const statusError = getOrderStatusError(statuses)
  if (!statusError) {
    return okVoidResult()
  }

  const failedIndexes = getFailedStatusIndexes(statuses)
  if (failedIndexes.length === 0 || failedIndexes.length === orders.length) {
    return { ok: false, error: statusError }
  }

  const retryResult = await exchange.order({
    orders: failedIndexes
      .map((i) => orders[i])
      .filter((order) => order != null),
    grouping: "positionTpsl",
    builder,
  })
  const retryError = getOrderStatusError(getOrderStatuses(retryResult))
  return retryError ? { ok: false, error: retryError } : okVoidResult()
}

export async function cancelOrders(
  signer: TradingSigner,
  params: {
    cancels: Array<{ orderAssetId: number; orderId: number }>
  }
): Promise<Result<void>> {
  if (params.cancels.length === 0) {
    return okVoidResult()
  }

  return withExchangeClient(signer, "cancelOrders", async (exchange) => {
    const result = await exchange.cancel({
      cancels: params.cancels.map((c) => ({ a: c.orderAssetId, o: c.orderId })),
    })
    const statusError = getOrderStatusError(getOrderStatuses(result))
    return statusError ? { ok: false, error: statusError } : okVoidResult()
  })
}

async function withExchangeClient<T>(
  signer: TradingSigner,
  actionName: string,
  action: (
    exchange: ReturnType<typeof createHyperliquidExchangeClient>
  ) => Promise<Result<T>>
): Promise<Result<T>> {
  try {
    return await action(createHyperliquidExchangeClient(signer))
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    logger.error(`HL ${actionName} failed: ${message}`, {
      error: String(error),
    })
    return { ok: false, error: message || "Unknown error" }
  }
}

function okVoidResult(): Result<void> {
  return { ok: true, data: undefined }
}

// HL constraint: price must satisfy 5 sig figs AND ≤ (6 − szDecimals) dp.
function formatHLPrice(price: number, szDecimals: number): string {
  if (price <= 0) {
    throw new Error("HL price must be positive")
  }
  const maxDecimals = Math.max(0, 6 - szDecimals)
  const sigFigRounded = Number(price.toPrecision(5))
  const formatted = Number(sigFigRounded.toFixed(maxDecimals)).toString()
  if (Number(formatted) === 0) {
    throw new Error("HL price is too small for this asset precision")
  }
  return formatted
}

export { formatHLSize }

function formatAbsoluteHLSize(size: string, szDecimals: number): string {
  const trimmed = size.trim()
  return formatHLSize(
    trimmed.startsWith("-") ? trimmed.slice(1) : trimmed,
    szDecimals
  )
}

function parseFilledOrderStatus(
  status: Record<string, unknown> | string | undefined,
  requestedSize?: string
): Result<{ avgPrice: string; totalSz: string }> {
  if (!status) {
    return { ok: false, error: "No status returned" }
  }
  if (typeof status === "string") {
    return { ok: false, error: `Unexpected status: ${status}` }
  }
  if ("filled" in status) {
    const filled = status.filled as { avgPx: string; totalSz: string }
    if (requestedSize != null && isPartialFill(filled.totalSz, requestedSize)) {
      return {
        ok: false,
        error: `Only closed ${filled.totalSz} of ${requestedSize}`,
      }
    }
    return {
      ok: true,
      data: { avgPrice: filled.avgPx, totalSz: filled.totalSz },
    }
  }
  if ("error" in status) {
    return { ok: false, error: String(status.error) }
  }
  return { ok: false, error: `Unexpected status: ${JSON.stringify(status)}` }
}

function isPartialFill(filledSize: string, requestedSize: string): boolean {
  const filled = Number(filledSize)
  const requested = Math.abs(Number(requestedSize))
  if (!Number.isFinite(filled) || !Number.isFinite(requested)) {
    return false
  }
  const epsilon = Math.max(requested * 1e-9, 1e-12)
  return filled + epsilon < requested
}

function parseOpenOrderStatus(
  status: Record<string, unknown> | string | undefined,
  entryOrder: EntryOrder
): Result<EntryOrderResult> {
  if (!status) {
    return { ok: false, error: "No status returned" }
  }
  if (typeof status === "string") {
    return { ok: false, error: `Unexpected status: ${status}` }
  }
  if ("filled" in status) {
    const filled = status.filled as {
      avgPx: string
      totalSz: string
      oid: number
    }
    const totalSz = Number(filled.totalSz)
    if (!Number.isFinite(totalSz) || totalSz <= 0) {
      return { ok: false, error: "Order did not fill" }
    }
    return {
      ok: true,
      data: {
        kind: "filled",
        avgPrice: filled.avgPx,
        totalSz: filled.totalSz,
        oid: filled.oid,
      },
    }
  }
  if ("resting" in status) {
    if (entryOrder.type === "limit") {
      const resting = status.resting as { oid: number }
      return {
        ok: true,
        data: {
          kind: "resting",
          oid: resting.oid,
        },
      }
    }
    return { ok: false, error: "IOC order did not fill" }
  }
  if ("error" in status) {
    return { ok: false, error: String(status.error) }
  }
  return { ok: false, error: `Unexpected status: ${JSON.stringify(status)}` }
}

function buildTPSLTriggerOrders(params: {
  orderAssetId: number
  szDecimals: number
  isLong: boolean
  size: string
  takeProfitPrice?: number
  stopLossPrice?: number
  takeProfitCloid?: ClientOrderId
  stopLossCloid?: ClientOrderId
}) {
  const orders: Array<ReturnType<typeof buildTPSLTriggerOrder>> = []
  if (params.takeProfitPrice) {
    orders.push(
      buildTPSLTriggerOrder({
        orderAssetId: params.orderAssetId,
        szDecimals: params.szDecimals,
        isLong: params.isLong,
        size: params.size,
        price: params.takeProfitPrice,
        cloid: params.takeProfitCloid,
        tpsl: "tp",
      })
    )
  }
  if (params.stopLossPrice) {
    orders.push(
      buildTPSLTriggerOrder({
        orderAssetId: params.orderAssetId,
        szDecimals: params.szDecimals,
        isLong: params.isLong,
        size: params.size,
        price: params.stopLossPrice,
        cloid: params.stopLossCloid,
        tpsl: "sl",
      })
    )
  }
  return orders
}

function buildTPSLTriggerOrder(params: {
  orderAssetId: number
  szDecimals: number
  isLong: boolean
  size: string
  price: number
  cloid?: ClientOrderId
  tpsl: "tp" | "sl"
}) {
  const triggerPx = formatHLPrice(params.price, params.szDecimals)
  const limitPx = formatHLPrice(
    getCloseLimitPrice(params.price, params.isLong),
    params.szDecimals
  )
  return {
    a: params.orderAssetId,
    b: !params.isLong,
    p: limitPx,
    s: formatAbsoluteHLSize(params.size, params.szDecimals),
    r: true,
    ...(params.cloid ? { c: params.cloid } : {}),
    t: {
      trigger: {
        isMarket: true,
        triggerPx,
        tpsl: params.tpsl,
      },
    },
  }
}

function getCloseLimitPrice(price: number, isLong: boolean): number {
  return isLong
    ? price * (1 - HL_MARKET_SLIPPAGE)
    : price * (1 + HL_MARKET_SLIPPAGE)
}

function getOrderStatuses(result: unknown) {
  const maybeStatuses = (
    result as {
      response?: {
        data?: {
          statuses?: Array<
            | { error: string }
            | "success"
            | "waitingForFill"
            | "waitingForTrigger"
            | Record<string, unknown>
          >
        }
      }
    }
  ).response?.data?.statuses

  return Array.isArray(maybeStatuses) ? maybeStatuses : undefined
}

function getFirstOrderStatus(result: unknown) {
  return getOrderStatuses(result)?.[0]
}

function getOrderStatusError(
  statuses:
    | Array<
        | { error: string }
        | "success"
        | "waitingForFill"
        | "waitingForTrigger"
        | Record<string, unknown>
      >
    | undefined
) {
  if (!statuses || statuses.length === 0) {
    return "No status returned"
  }

  for (const status of statuses) {
    if (typeof status === "object" && "error" in status) {
      return String(status.error)
    }
  }
  return null
}

function getAttachedTPSLError(
  statuses:
    | Array<
        | { error: string }
        | "success"
        | "waitingForFill"
        | "waitingForTrigger"
        | Record<string, unknown>
      >
    | undefined,
  expectedLegCount: number
): string | null {
  if (expectedLegCount === 0) {
    return null
  }
  const legStatuses = statuses?.slice(1)
  if (!legStatuses || legStatuses.length < expectedLegCount) {
    return "No TP/SL status returned"
  }
  return getOrderStatusError(legStatuses.slice(0, expectedLegCount))
}

function getFailedStatusIndexes(
  statuses:
    | Array<
        | { error: string }
        | "success"
        | "waitingForFill"
        | "waitingForTrigger"
        | Record<string, unknown>
      >
    | undefined
) {
  if (!statuses) return []
  return statuses.flatMap((status, index) =>
    typeof status === "object" && "error" in status ? [index] : []
  )
}
